import type { PlanCode } from './apiContracts';
import type { PrototypeApiClient } from './apiClient';
import { presentMediaAccessDecision, resolveMediaAccessDecision } from './mediaPlayerAccessGate';
import { getChapterCount, navigateAndPlayAudio, navigateToVideoChapter } from './mediaPlayerChapterPlayback';
import { clearVideoEndedMediaSession, mountedFor } from './mediaPlayerCore';
import { classifyMediaPlaybackBoundary, decideMediaPlaybackEnd } from './mediaPlaybackEndedDecision';
import type { MediaKind } from './mediaPlayerTypes';
import { presentFinalVideoChapter, recordCompletedVideoChapter } from './mediaPlayerVideoEnded';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';
import { getTopic } from './topicStore';
import { showToast } from './toastController';
import { scheduleHomeRecentReconcile } from './homeRecentHydrator';
import { previewNowPlayingChapter } from './playerUiController';
import {
	beginAudioHandoffCommandIntent,
	isAudioHandoffIntentCurrent,
	takePendingAudioResume,
	targetAudioHandoffIntent,
} from './audioHandoffController';

export interface MediaEndedEventDetail {
	kind: MediaKind;
	topicId: string;
	chapterIndex: number;
	element: HTMLAudioElement | HTMLVideoElement;
}

const mediaEndedAbort = new WeakMap<object, AbortController>();
const wiredEndedRoots = new WeakSet<object>();

export function wireMediaEndedCommands(root: Root, api: PrototypeApiClient, plan: PlanCode): void {
	const key = root as unknown as object;
	if (wiredEndedRoots.has(key)) return;
	const abort = typeof AbortController === 'undefined' ? null : new AbortController();
	if (abort) mediaEndedAbort.set(key, abort);
	wiredEndedRoots.add(key);
	const options = abort ? ({ signal: abort.signal } as AddEventListenerOptions) : undefined;
	(root as Document | HTMLElement).addEventListener('deepmox:media-ended', ((event: Event) => {
		const detail = (event as CustomEvent<MediaEndedEventDetail>).detail;
		if (detail?.kind !== 'audio' && detail?.kind !== 'video') return;
		void handleMediaEnded(root, currentApi(root, api), currentPlan(root, plan), detail);
	}) as EventListener, options);
	(root as Document | HTMLElement).addEventListener('deepmox:audio-resume-request', (() => {
		const pending = takePendingAudioResume(root);
		if (!pending) return;
		// The pending target is always an auto-advance chapter, so it starts
		// from 0:00 like the in-place handoff path instead of an old cursor.
		navigateAndPlayAudio(root, pending.topicId, pending.chapterIndex, currentPlan(root, plan), currentApi(root, api), undefined, { resume: 'start' });
	}) as EventListener, options);
	(root as Document | HTMLElement).addEventListener('deepmox:audio-handoff-ui', ((event: Event) => {
		const detail = (event as CustomEvent<{ topicId?: string; chapterIndex?: number }>).detail;
		const chapterIndex = detail?.chapterIndex;
		if (!Number.isInteger(chapterIndex) || !chapterIndex || typeof document === 'undefined' || document.visibilityState === 'hidden') return;
		void import('./playerUiController').then((player) => player.showPlayer(root));
	}) as EventListener, options);
}

export function detachMediaEndedCommands(root: Root): void {
	const key = root as unknown as object;
	mediaEndedAbort.get(key)?.abort();
	mediaEndedAbort.delete(key);
	wiredEndedRoots.delete(key);
}

function currentApi(root: Root, fallback: PrototypeApiClient): PrototypeApiClient {
	return getRuntime(root)?.api ?? fallback;
}

function currentPlan(root: Root, fallback: PlanCode): PlanCode {
	return getRuntime(root)?.currentPlan ?? fallback;
}

async function handleMediaEnded(
	root: Root,
	api: PrototypeApiClient,
	plan: PlanCode,
	detail: MediaEndedEventDetail,
): Promise<void> {
	if (!isCurrentEndedMedia(root, detail)) return;
	// Either way this chapter is now finished for progress purposes: give the
	// completed write a moment to land, then read the rail's new order back so
	// Home reflects the finish without waiting for the next re-entry.
	scheduleHomeRecentReconcile(root, api);
	const audioIntent = detail.kind === 'audio' ? beginAudioHandoffCommandIntent(root) : null;
	if (detail.kind === 'video') recordCompletedVideoChapter(root, detail.topicId, detail.chapterIndex);
	let totalChapters: number;
	try {
		totalChapters = await getChapterCount(api, detail.topicId);
	} catch {
		if (detail.kind === 'video') clearVideoEndedMediaSession(root, detail.element);
		return;
	}
	if (!isCurrentEndedMedia(root, detail) || (audioIntent && !isAudioHandoffIntentCurrent(root, audioIntent))) return;
	const boundary = classifyMediaPlaybackBoundary({
		currentChapter: detail.chapterIndex,
		totalChapters,
	});
	if (boundary.kind === 'stop') {
		if (detail.kind === 'video' && boundary.reason === 'final_chapter') {
			presentFinalVideoChapter(root, detail.topicId, detail.chapterIndex);
		}
		if (detail.kind === 'video') clearVideoEndedMediaSession(root, detail.element);
		return;
	}
	const accessDecision = resolveMediaAccessDecision(root, detail.kind, detail.topicId, boundary.nextChapter);
	const decision = decideMediaPlaybackEnd({
		currentChapter: detail.chapterIndex,
		totalChapters,
		accessDecision,
	});
	if (decision.kind === 'gate') {
		detail.element.pause();
		if (detail.kind === 'audio') {
			showToast({ type: 'warning', message: 'Next chapter requires an upgrade.', durationMs: 7_000 });
		} else presentMediaAccessDecision(root, decision.decision, 'auto-advance');
		if (detail.kind === 'video') clearVideoEndedMediaSession(root, detail.element);
		return;
	}
	if (decision.kind === 'advance') {
		// Auto-advance starts the next chapter at 0:00 on both modality paths —
		// a stale half-played cursor for the target must not resurrect here.
		const runtime = getRuntime(root);
		if (runtime) {
			const nextKey = `${detail.topicId}:${decision.nextChapter}`;
			runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [nextKey]: 0 };
			runtime.resumeProgressByChapter = { ...(runtime.resumeProgressByChapter ?? {}), [nextKey]: 0 };
		}
		if (detail.kind === 'audio') {
			// The dock must read the next chapter while the token round-trip is
			// still in flight; the authoritative mount overwrites this.
			const nextTitle = getTopic(detail.topicId)?.previewChapters
				?.find((chapter) => chapter.index === decision.nextChapter)?.title
				|| `Chapter ${decision.nextChapter}`;
			previewNowPlayingChapter(root, {
				index: decision.nextChapter,
				title: nextTitle,
				topicTitle: getTopic(detail.topicId)?.title || detail.topicId,
				topicSlug: detail.topicId,
				topicSubtitle: getTopic(detail.topicId)?.subtitle || '',
			});
			const navigationIntent = targetAudioHandoffIntent(root, audioIntent!, { topicId: detail.topicId, chapterIndex: decision.nextChapter });
			if (navigationIntent) navigateAndPlayAudio(root, detail.topicId, decision.nextChapter, plan, api, navigationIntent, { navigation: 'preserve-view', resume: 'start' });
		}
		else navigateToVideoChapter(root, detail.topicId, decision.nextChapter, plan, api);
	}
}

function isCurrentEndedMedia(root: Root, detail: MediaEndedEventDetail): boolean {
	const media = mountedFor(root, detail.kind);
	// The page can be browsing another Topic while this element is still
	// audible. The mounted source is therefore the only end-boundary identity.
	if (media?.element !== detail.element || media.topicId !== detail.topicId || media.chapterIndex !== detail.chapterIndex) return false;
	return true;
}
